import { useCallback, useEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";

import { useTranslation } from "react-i18next";

import {
  Minus,
  Square as SquareIcon,
  Copy,
  X,
  User,
  Settings,
  LogOut,
  Unplug,
} from "lucide-react";

import { useAtom } from "jotai";

import { useWindowControls } from "./useWindowControls";

import { accessKeyAtom, userIdAtom, userAtom } from "@/atoms/auth.atoms";

import { participantIdAtom, romIdAtom } from "@/atoms/rom.atoms";

import { serverHostAtom } from "@/atoms/settings.atoms";

import { stopPublishCallbackAtom } from "@/atoms/connection.atoms";

import authService from "@/services/auth.service";

import { romService } from "@/services/rom.service";

import LogoImage from "@/assets/logo.png";

import "./CustomTitleBar.css";

const TITLEBAR_HEIGHT = 35;

export function CustomTitleBar({ children }: { children?: React.ReactNode }) {
  const {
    isElectron,
    isMaximized,
    handleMinimize,
    handleToggleMaximize,
    handleClose,
  } = useWindowControls();

  const { t } = useTranslation("auth");

  const [user] = useAtom(userAtom);

  const [, setAccessKey] = useAtom(accessKeyAtom);

  const [, setUserId] = useAtom(userIdAtom);

  const [, setUser] = useAtom(userAtom);

  const [romId, setRomId] = useAtom(romIdAtom);

  const [participantId, setParticipantId] = useAtom(participantIdAtom);

  const [serverHost] = useAtom(serverHostAtom);

  const hasActiveSession = Boolean(serverHost) || Boolean(romId);

  // Toggle has-titlebar class on <html> so CSS can adjust content height

  useEffect(() => {
    if (isElectron) {
      document.documentElement.classList.add("has-titlebar");

      return () => document.documentElement.classList.remove("has-titlebar");
    }
  }, [isElectron]);

  const [stopPublish] = useAtom(stopPublishCallbackAtom);

  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);

  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  // Close menu on outside click

  useEffect(() => {
    if (!menuOpen) return;

    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // Calculate menu position when opening

  useEffect(() => {
    if (menuOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      setMenuPos({
        top: rect.bottom + 4,

        right: window.innerWidth - rect.right,
      });
    }
  }, [menuOpen]);

  const handleDisconnect = useCallback(() => {
    setMenuOpen(false);
    window.dispatchEvent(new CustomEvent("session:disconnect"));
  }, []);

  const handleLogout = useCallback(async () => {
    setMenuOpen(false);

    if (romId && participantId) {
      try {
        await romService.leave(romId, participantId);
      } catch {
        /* best-effort; scheduler will reap */
      }
    }

    try {
      await authService.logout();
    } catch {
      /* ignore */
    }

    localStorage.removeItem("@Guest/accessKey");

    localStorage.removeItem("@Guest/userId");

    setAccessKey(null);

    setUserId(null);

    setUser(null);

    setRomId(null);

    setParticipantId(null);

    window.location.pathname = "/";
  }, [
    setAccessKey,
    setUserId,
    setUser,
    romId,
    participantId,
    setRomId,
    setParticipantId,
  ]);

  // Only render custom titlebar inside Electron on Windows/Linux

  if (!isElectron) return <>{children}</>;

  const separatorHeight = Math.round(TITLEBAR_HEIGHT * 0.5);

  const displayName = user?.name || user?.email?.split("@")[0] || null;

  const avatarUrl = user?.avatar || null;

  return (
    <div
      className="titlebar titlebar-glass z-[10000] flex items-center justify-between select-none text-foreground"
      style={{ height: `${TITLEBAR_HEIGHT}px` }}
    >
      {/* Left: logo + name */}
      <div className="flex items-center pl-4 gap-2">
        <img
          src={LogoImage}
          alt="Eriol"
          className="h-5 w-5 object-contain shrink-0"
        />
        <span className="text-xs font-medium opacity-70">Guest</span>
      </div>

      {/* Center: stop button or optional slot */}
      <div className="titlebar flex-1 flex items-center justify-center">
        {stopPublish ? (
          <div className="titlebar-nodrag">
            <button
              onClick={stopPublish}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-[11px] font-medium hover:bg-red-500/20 active:bg-red-500/25 transition-colors cursor-pointer"
            >
              <SquareIcon className="w-2.5 h-2.5 fill-current" />
              {t("stopStreaming", {
                ns: "streaming",
                defaultValue: "Stop Streaming",
              })}
            </button>
          </div>
        ) : children ? (
          <div className="titlebar-nodrag">{children}</div>
        ) : null}
      </div>

      {/* Right: profile + window controls */}
      <div className="flex items-center">
        {displayName && (
          <>
            <div className="titlebar-nodrag">
              <button
                ref={triggerRef}
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-3 h-full hover:bg-white/5 active:bg-white/8 transition-colors rounded-sm"
                style={{ height: `${TITLEBAR_HEIGHT}px` }}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    className="w-5 h-5 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-3 h-3 text-primary" />
                  </div>
                )}
                <span className="text-[11px] text-muted-foreground">
                  {displayName}
                </span>
              </button>

              {menuOpen &&
                createPortal(
                  <div
                    ref={menuRef}
                    className="fixed w-44 rounded-lg border border-white/10 bg-[rgba(10,10,20,0.95)] backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] py-1 z-[99999]"
                    style={{
                      top: `${menuPos.top}px`,
                      right: `${menuPos.right}px`,
                    }}
                  >
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        window.location.pathname = "/settings";
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] text-muted-foreground hover:text-foreground hover:bg-white/5 active:bg-white/8 transition-colors"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      {t("settings", {
                        ns: "settings",
                        defaultValue: "Settings",
                      })}
                    </button>
                    {hasActiveSession && (
                      <>
                        <div className="h-px bg-white/8 mx-2 my-0.5" />
                        <button
                          onClick={handleDisconnect}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] text-muted-foreground hover:text-foreground hover:bg-white/5 active:bg-white/8 transition-colors"
                        >
                          <Unplug className="w-3.5 h-3.5" />
                          {t("disconnectSession", {
                            defaultValue: "Desconectar",
                          })}
                        </button>
                      </>
                    )}
                    <div className="h-px bg-white/8 mx-2 my-0.5" />
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] text-destructive hover:bg-destructive/10 active:bg-destructive/15 transition-colors"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      {t("logout", { defaultValue: "Logout" })}
                    </button>
                  </div>,

                  document.body,
                )}
            </div>
            <div
              className="titlebar-separator"
              style={{ height: `${separatorHeight}px` }}
            />
          </>
        )}

        <button
          className="titlebar-winbtn"
          style={{ height: `${TITLEBAR_HEIGHT}px` }}
          title="Minimize"
          onClick={handleMinimize}
        >
          <Minus size={10} />
        </button>

        <button
          className="titlebar-winbtn"
          style={{ height: `${TITLEBAR_HEIGHT}px` }}
          title={isMaximized ? "Restore" : "Maximize"}
          onClick={handleToggleMaximize}
        >
          {isMaximized ? <Copy size={10} /> : <SquareIcon size={10} />}
        </button>

        <button
          className="titlebar-winbtn titlebar-close"
          style={{ height: `${TITLEBAR_HEIGHT}px` }}
          title="Close"
          onClick={handleClose}
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}
